import { EventBySlugOutput } from "~/types/EventTypesInferred";
import { useEffect, useState } from "react";
import { api } from "~/utils/api";
import { toast } from "react-toastify";
import Linkify from "linkify-react";
import { If } from "../v3/Conditional";
import { Switch } from "../v3/themed/Switch";
import { PrimaryButton, UnderLineButton } from "../themed/customButtons";
import Modal from "../v3/themed/Modal";
import { Button } from "../v3/themed/Button";

type TermsItem = NonNullable<
    EventBySlugOutput["community"]
>["reimbursementTerms"][number];

const ReimbursementTermsModal = ({
    isVisible,
    setIsVisible,
    onCompletion,
    event,
}: {
    isVisible: boolean;
    setIsVisible: (show: boolean) => void;
    onCompletion: (showReimbursementModal: boolean) => void;
    event?: EventBySlugOutput;
}) => {
    const checkList = event?.community?.reimbursementTerms || [];
    const [checked, setChecked] = useState<TermsItem[]>(
        checkList.filter((t) => !!t.default),
    );
    const [isRequiredChecked, setIsRequiredChecked] = useState(false);
    const confirmReimbursementTerms =
        api.user.confirmReimbursementTerms.useMutation();

    const handleCheck = (item: TermsItem, isChecked: boolean) => {
        var updatedList = [...checked];
        if (isChecked) {
            updatedList = [...checked, item];
        } else {
            updatedList.splice(checked.indexOf(item), 1);
        }
        setChecked(updatedList);
    };

    useEffect(() => {
        const requiredUnchecked: TermsItem[] = checkList.reduce((r, e) => {
            if (e.isRequired && !checked.find((ce) => ce.id === e.id))
                r.push(e);
            return r;
        }, new Array<TermsItem>());
        setIsRequiredChecked(!requiredUnchecked.length);
    }, [checked]);

    const handleConfirmation = async () => {
        if (!isRequiredChecked) {
            toast.error("Community terms not accepted");
            return;
        }
        toast.loading(`Accepting community terms`, {
            toastId: "confirm-terms",
        });

        await confirmReimbursementTerms.mutateAsync(
            {
                communityId: event?.community?.id,
                terms: checked.map((c) => c.id),
            },
            {
                onSuccess: (data) => {
                    toast.success(`Accepted community terms`, {
                        autoClose: 1000,
                    });
                    onCompletion(data.success);
                },
                onError: () => {
                    toast.error(`Error accepting community terms`, {
                        autoClose: 1000,
                    });
                },
                onSettled: () => {
                    toast.dismiss("confirm-terms");
                },
            },
        );
    };

    return (
        <Modal
            open={isVisible}
            onOpenChange={setIsVisible}
            title={event?.community?.reimbursementTermsDescription?.heading}
            description={
                event?.community?.reimbursementTermsDescription?.paragraph
            }
        >
            <div className="flex max-h-[50vh] flex-col justify-start overflow-auto text-start sm:max-h-[60vh]">
                {checkList.map((item) => (
                    <p
                        key={item.id}
                        className="mt-4 flex flex-row items-center gap-4 text-white"
                    >
                        <span className="overflow-hidden break-words">
                            <Linkify
                                options={{
                                    className:
                                        "text-wrap text-white hover:underline",
                                    target: "_blank",
                                }}
                            >
                                {item.text}
                                <If condition={item.isRequired}>
                                    <span className="align-super text-xs text-v3-R500">
                                        {" "}
                                        (*required)
                                    </span>
                                </If>
                            </Linkify>
                        </span>
                        <Switch
                            id="use-generic-social-prompt"
                            defaultChecked={!!item.default}
                            onCheckedChange={(val) => {
                                handleCheck(item, val);
                            }}
                        />
                    </p>
                ))}
            </div>
            <div className="mt-4 flex flex-col gap-4 sm:flex-row">
                <Button
                    className="w-full sm:ml-4 sm:w-auto"
                    variant="tertiary"
                    onClick={() => setIsVisible(false)}
                >
                    Nevermind
                </Button>
                <Button
                    className="w-full sm:w-[33%]"
                    variant="default"
                    disabled={!isRequiredChecked}
                    onClick={handleConfirmation}
                >
                    Proceed
                </Button>
            </div>
        </Modal>
    );
};

export default ReimbursementTermsModal;
